import { hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { hasOptionalPropertyOfType } from "@ciaopeople/cp-shatsu/CustomTypes";
import { isArrayOf, isBoolean, isNumber, isObject, isString, getEnumTypeChecker, inEnum } from "@ciaopeople/cp-shatsu/TypeChecking";
import { isNotificationTopic, NotificationTopic } from "../commondata/TopicTreeData.js";
import { Image, isImage } from "./UserDataTypes.js";
import { SOURCE_ELEMENT } from "../../stats/CTATypes.js";

export enum TopicType {
    CATEGORY = "cat",
    AUTHOR = "aut",
    METEO_LOCATION = "met",
    AREA = "are",
    CHANNEL = "cha",
    STORY = "sto",
    CONTENT = "cnt",
    NEWSBOX = "nsb"
}

export interface UserActionExtraInfo {

    source_element?: SOURCE_ELEMENT
}


export enum TopicGroupType {

    MIXED = "mixed",
    BOOKMARK = "bookmark",
    NOTIFICATION = "notification",
    TOPIC = "topic",
    CONTENT = "content",
    UNSPECIFIED = "unspecified"

}

export type ExtendedTopicId = `${TopicType}-${string}`;

export function getTopicTypeFromExtendedTopicId(ext: ExtendedTopicId): TopicType {
    const exploded = ext.split("-", 2);
    return exploded[0] as TopicType;
}

export function isExtendedTopicId(obj: any): obj is ExtendedTopicId {
    if ( isString(obj) ) {
        const exploded = obj.split("-", 2);
        return exploded.length == 2 && !!exploded[1].match(/\S/) && inEnum(exploded[0], TopicType);
    }
    return false;
}

export interface TopicQueryByUrl {
    url: string,
    hint?: TopicType
}

export function isTopicQueryByUrl(obj: any): obj is TopicQueryByUrl {
    return isObject(obj) && isString(obj["url"]) && hasOptionalPropertyOfType(obj, "hint", getEnumTypeChecker(TopicType) );
}

export interface TopicQueryByShortId {
    id: string,
    hint: TopicType
}

export function isTopicQueryByShortId(obj: any): obj is TopicQueryByShortId {
    return isObject(obj) && isString(obj["id"]) && inEnum(obj['hint'], TopicType);
}


export interface TopicQueryByWordpressId {
    wpid: string,
    hint: TopicType
}

export function isTopicQueryByWordpressId(obj: any): obj is TopicQueryByWordpressId {
    return isObject(obj) && isString(obj["wpid"]) && inEnum(obj['hint'], TopicType);
}


export interface TopicQueryByExtendedId {
    extId: ExtendedTopicId
}

export function isTopicQueryByExtendedId(obj: any): obj is TopicQueryByExtendedId {
    return isObject(obj) && isExtendedTopicId(obj["extId"]);
}




export type TopicQueryObj = TopicQueryByUrl | TopicQueryByShortId | TopicQueryByWordpressId | TopicQueryByExtendedId;


export function isTopicQueryObj(obj: any): obj is TopicQueryObj {

    return isTopicQueryByUrl(obj) || isTopicQueryByWordpressId(obj) ||
        isTopicQueryByShortId(obj) || isTopicQueryByExtendedId(obj);
}

export enum ContentRestrictionCriteria {

    INHERIT = "i",
    RESTRICT_TO_MEMBERS = "mr",
    ACCESSIBLE_TO_ALL = "o"

}

export interface TopicObj extends NotificationTopic {
    description?: string,
    image?: Image,
    color?: number,
    meta_info?: { [k: string]: string },
    topic_set?: TopicObj[],
    descendants?: TopicObj[],
    layout: string,
    parent?: TopicObj,
    menu?: TopicObj[],
    followable: boolean,
    alternative_urls?: string[],
    followed?: boolean,
    backgroundImage?: Image,
    subscribeable: boolean,
    subscribed?: boolean,
    subscriptionRestriction: ContentRestrictionCriteria
}

export function isTopicObj(ele: any): ele is TopicObj {
    return isObject(ele) && 
        hasOwnProperty(ele, "layout") && isString(ele.layout) &&
        hasOwnProperty(ele, "followable") && isBoolean(ele.followable) &&
        hasOwnProperty(ele, "subscribeable") && isBoolean(ele.subscribeable) &&
        hasOwnProperty(ele, "subscriptionRestriction") && inEnum(ele.subscriptionRestriction,ContentRestrictionCriteria) &&
        ( !hasOwnProperty(ele, "description") || isString(ele.description) ) &&
        ( !hasOwnProperty(ele, "image") || isImage(ele.image) ) &&
        ( !hasOwnProperty(ele, "color") || isNumber(ele.color) ) &&
        ( !hasOwnProperty(ele, "meta_info") || isObject(ele.meta_info) ) &&
        ( !hasOwnProperty(ele, "topic_set") || isArrayOf(ele.topic_set, isTopicObj) ) &&
        ( !hasOwnProperty(ele, "descendants") || isArrayOf(ele.descendants, isTopicObj) ) &&
        ( !hasOwnProperty(ele, "parent") || isTopicObj(ele.parent) ) &&
        ( !hasOwnProperty(ele, "menu") || isArrayOf(ele.menu, isTopicObj) ) &&
        ( !hasOwnProperty(ele, "alternative_urls") || isArrayOf(ele.alternative_urls, isString) ) &&
        ( !hasOwnProperty(ele, "followed") || isBoolean(ele.followed) ) &&
        ( !hasOwnProperty(ele, "subscribed") || isBoolean(ele.subscribed) ) &&
        ( !hasOwnProperty(ele, "backgroundImage") || isImage(ele.backgroundImage) );
        
}


export type GroupedResolvedTopicObjs = {
    authors?: TopicObj[];
    categories?: TopicObj[];
    locations?: TopicObj[];
    stories?: TopicObj[];
    channels?: TopicObj[];
};


export type GroupedTopicObjs = {
    [k in keyof GroupedResolvedTopicObjs]: ExtendedTopicId[]
};

export function classifyExtendedTopicIds(ext: ExtendedTopicId[]): GroupedTopicObjs {

    const zeroValue: GroupedTopicObjs = {
        categories: [],
        authors: [],
        stories: [],
        channels: [],
    };
    return ext.reduce((acc, el) => {
        const tt = getTopicTypeFromExtendedTopicId(el);
        switch(tt) {
            case TopicType.CATEGORY:
                acc.categories.push(el);
                break;
            case TopicType.CHANNEL:
                acc.channels.push(el);
                break;
            case TopicType.AUTHOR:
                acc.authors.push(el);
                break;
            case TopicType.STORY:
                acc.stories.push(el);
                break;
        }
       
        return acc;
    }, zeroValue);

}




export interface FollowResponseObject {

    isFirst: boolean,
    topic?: NotificationTopic
}




export function isFollowResponseObject(obj: any): obj is FollowResponseObject {
    return isObject(obj) && isBoolean(obj.isFirst) && hasOptionalPropertyOfType(obj, "topic", isNotificationTopic);
}





export interface TopicFollowCheckRequest {

    queries: TopicQueryObj[]
}

export function isTopicFollowCheckRequest(obj: any): obj is TopicFollowCheckRequest {
    return isObject(obj) && isArrayOf(obj.queries, isTopicQueryObj);
}


export type TopicSubscriptionCheckRequest = TopicFollowCheckRequest


export function isTopicSubscriptionCheckRequest(obj: any): obj is TopicSubscriptionCheckRequest {
    return isTopicFollowCheckRequest(obj);
}


export interface TopicQueryObjWithFollow {
    query: TopicQueryObj,
    followed: boolean,
    restriction?: ContentRestrictionCriteria,
    url?: string,
    extra_info?: UserActionExtraInfo
}

export function isTopicQueryObjWithFollow(obj: any): obj is TopicQueryObjWithFollow {
    return isObject(obj) && isBoolean(obj.followed) 
    && isTopicQueryObj(obj.query) &&
    (!("restriction" in obj) || inEnum(obj.restriction,ContentRestrictionCriteria));
} 

export interface TopicFollowCheckResponse {
    topics: TopicQueryObjWithFollow[]
}

export function isTopicFollowCheckResponse(obj: any): obj is TopicFollowCheckResponse {
    return isObject(obj) && isArrayOf(obj.topics, isTopicQueryObjWithFollow);
}





export interface TopicQueryObjWithSubscribe {

    query: TopicQueryObj
    subscribed: boolean,
    restriction: ContentRestrictionCriteria,
    url?: string,
    extra_info?: UserActionExtraInfo

}


export function isTopicQueryObjWithSubscribe(obj: any): obj is TopicQueryObjWithSubscribe {
    return isObject(obj) && 
        ("subscribed" in obj && isBoolean(obj.subscribed)) && 
            isTopicQueryObj(obj.query) &&
        ("restriction" in obj && inEnum(obj.restriction,ContentRestrictionCriteria));
} 


export interface TopicSubscriptionCheckResponse {
    topics: TopicQueryObjWithSubscribe[]
}

export function isTopicSubscriptionCheckResponse(obj: any): obj is TopicSubscriptionCheckResponse {
    return isObject(obj) && isArrayOf(obj.topics, isTopicQueryObjWithSubscribe);
}


export interface RecommendedResponse {
    contents: { [k: string]: any }[]
}

export function isRecommendedResponse(obj: any): obj is RecommendedResponse {
    return isObject(obj) && isArrayOf(obj.contents, isObject);
}

export interface RecommendedCount {
    count: number
}

export function isRecommendedCount(obj: any): obj is RecommendedCount {
    return isObject(obj) && isNumber(obj.count);
}

